import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, Smartphone, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MockPaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const transactionRef = searchParams.get("ref");
  
  const [network, setNetwork] = useState("mtn");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("select"); // select, processing, result

  const handleMockPayment = async (success) => {
    setLoading(true);
    setStep("processing");
    
    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      await axios.post(`${API}/payment/mock-complete/${transactionRef}?success=${success}`);
      
      if (success) {
        navigate(`/payment/success?ref=${transactionRef}`);
      } else {
        navigate(`/payment/failure?ref=${transactionRef}&reason=mock_declined`);
      }
    } catch (error) {
      console.error("Mock payment error:", error);
      navigate(`/payment/failure?ref=${transactionRef}&reason=system_error`);
    } finally {
      setLoading(false);
    }
  };

  if (!transactionRef) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Invalid payment session</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center border-b">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="font-heading text-xl">
            {step === "processing" ? "Processing Payment..." : "Mock Payment Gateway"}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            {step === "processing" 
              ? "Please wait while we process your payment"
              : "This is a development/test payment page"
            }
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          {step === "select" && (
            <>
              <div className="mb-6">
                <Label className="text-sm font-medium mb-3 block">Select Network</Label>
                <RadioGroup value={network} onValueChange={setNetwork} className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:border-primary cursor-pointer">
                    <RadioGroupItem value="mtn" id="mtn" />
                    <Label htmlFor="mtn" className="flex-1 cursor-pointer">
                      <span className="font-medium">MTN Mobile Money</span>
                      <span className="block text-xs text-muted-foreground">Fastest processing</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:border-primary cursor-pointer">
                    <RadioGroupItem value="vodafone" id="vodafone" />
                    <Label htmlFor="vodafone" className="flex-1 cursor-pointer">
                      <span className="font-medium">Vodafone Cash</span>
                      <span className="block text-xs text-muted-foreground">Standard processing</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:border-primary cursor-pointer">
                    <RadioGroupItem value="airteltigo" id="airteltigo" />
                    <Label htmlFor="airteltigo" className="flex-1 cursor-pointer">
                      <span className="font-medium">AirtelTigo Money</span>
                      <span className="block text-xs text-muted-foreground">Standard processing</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-accent">Test Mode</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Choose to simulate a successful or failed payment below.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => handleMockPayment(true)}
                  className="bg-success hover:bg-success/90 h-14"
                  disabled={loading}
                  data-testid="mock-success-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Simulate Success
                </Button>
                <Button
                  onClick={() => handleMockPayment(false)}
                  variant="destructive"
                  className="h-14"
                  disabled={loading}
                  data-testid="mock-fail-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Simulate Failure
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground mt-6">
                Transaction: {transactionRef}
              </p>
            </>
          )}

          {step === "processing" && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="font-medium">Processing your payment...</p>
              <p className="text-sm text-muted-foreground mt-2">
                In production, you would receive a prompt on your phone
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
